import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Play, Pause, RotateCcw } from 'lucide-react';
import { useSession } from '../context/SessionContext';

const FALLBACK_API_URL = 'https://taraanaya-prod-production.up.railway.app';
const API_URL = process.env.REACT_APP_BACKEND_URL || FALLBACK_API_URL;

// Domain configuration - same colors as past story mahadashas
const DOMAINS = {
  health: { key: 'health', name: 'Health & Longevity', icon: '❤️', color: '#ef4444' },
  career: { key: 'career', name: 'Career & Success', icon: '💼', color: '#3b82f6' },
  education: { key: 'education', name: 'Education & Knowledge', icon: '📚', color: '#8b5cf6' },
  wealth: { key: 'wealth', name: 'Wealth & Prosperity', icon: '💰', color: '#22c55e' },
  marriage: { key: 'marriage', name: 'Love & Marriage', icon: '💍', color: '#ec4899' },
  family: { key: 'family', name: 'Family & Children', icon: '👨‍👩‍👧', color: '#f59e0b' },
  travel: { key: 'travel', name: 'Travel & Foreign', icon: '✈️', color: '#06b6d4' },
  spiritual: { key: 'spiritual', name: 'Spirituality & Moksha', icon: '🕉️', color: '#a855f7' }
};

// Planet icons for mahadasha display
const PLANET_ICONS = {
  Sun: '☉', Moon: '☽', Mars: '♂', Mercury: '☿',
  Jupiter: '♃', Venus: '♀', Saturn: '♄', Rahu: '☊', Ketu: '☋'
};

// Planet colors
const PLANET_COLORS = {
  Sun: '#FFD700', Moon: '#C0C0C0', Mars: '#DC143C', Mercury: '#32CD32',
  Jupiter: '#FFA500', Venus: '#FF69B4', Saturn: '#4A4A4A', Rahu: '#4B0082', Ketu: '#006400'
};

const LANGUAGES = ['hindi', 'hinglish', 'english'];
const MAX_CHUNK_CHARS = 500;

// Split text into chunks
const splitTextIntoChunks = (text, maxChars) => {
  if (!text || text.length <= maxChars) return [text];
  
  const chunks = [];
  let remaining = text;
  
  while (remaining.length > 0) {
    if (remaining.length <= maxChars) {
      chunks.push(remaining.trim());
      break;
    }
    
    let splitPoint = remaining.lastIndexOf('. ', maxChars);
    if (splitPoint === -1 || splitPoint < maxChars * 0.5) {
      splitPoint = remaining.lastIndexOf(' ', maxChars);
    }
    if (splitPoint === -1) splitPoint = maxChars;
    
    chunks.push(remaining.substring(0, splitPoint + 1).trim());
    remaining = remaining.substring(splitPoint + 1).trim();
  }
  
  return chunks.filter(c => c.length > 0);
};

const FutureStoryUnified = () => {
  const navigate = useNavigate();
  const { domainKey } = useParams();
  const { isPremium } = useSession();
  
  const domain = DOMAINS[domainKey] || DOMAINS.health;
  
  // Core states
  const [isLoading, setIsLoading] = useState(true);
  const [selectedLanguage, setSelectedLanguage] = useState('hinglish');
  const [isPlaying, setIsPlaying] = useState(false);
  const [storyComplete, setStoryComplete] = useState(false);
  
  // Story data
  const [storyData, setStoryData] = useState(null);
  const [futureMahadashas, setFutureMahadashas] = useState([]);
  const [audioChunks, setAudioChunks] = useState({ hindi: [], hinglish: [], english: [] });
  
  // Playback states
  const [currentChunkIndex, setCurrentChunkIndex] = useState(0);
  const [currentMahadashaIndex, setCurrentMahadashaIndex] = useState(-1);
  const [displayedText, setDisplayedText] = useState('');
  const [playbackPosition, setPlaybackPosition] = useState({ hindi: 0, hinglish: 0, english: 0 });
  
  // Refs
  const audioRef = useRef(null);
  const abortRef = useRef(false);
  
  // Get current mahadasha for display
  const currentMahadasha = currentMahadashaIndex >= 0 && futureMahadashas[currentMahadashaIndex]
    ? futureMahadashas[currentMahadashaIndex]
    : { lord: '', years: '', color: domain.color };

  // Update displayed text
  const updateDisplayedText = useCallback((text) => {
    setDisplayedText(text || '');
  }, []);

  // Stop audio
  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.src = '';
    }
  }, []);

  // Generate audio for chunk
  const getAudioForChunk = useCallback(async (text, lang) => {
    try {
      const response = await fetch(`${API_URL}/api/tts/edge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, language: lang })
      });
      
      const data = await response.json();
      if (data.success && data.audio) {
        return `data:audio/mp3;base64,${data.audio}`;
      }
      return null;
    } catch (error) {
      console.error('TTS error:', error);
      return null;
    }
  }, []);

  // Play audio chunk
  const playAudioChunk = useCallback(async (audioUrl, chunkText) => {
    return new Promise((resolve) => {
      if (!audioRef.current) {
        audioRef.current = new Audio();
      }
      
      const audio = audioRef.current;
      audio.src = audioUrl;
      
      audio.onended = () => resolve(true);
      audio.onerror = () => resolve(false);
      
      audio.play().catch(() => resolve(false));
    });
  }, []);

  // Play story
  const playStory = useCallback(async () => {
    const lang = selectedLanguage;
    const chunks = audioChunks[lang];
    
    if (!chunks || chunks.length === 0) {
      console.log('No chunks available');
      return;
    }
    
    const startPos = playbackPosition[lang] || 0;
    
    if (startPos >= chunks.length) {
      // Reset and play from start
      setPlaybackPosition(prev => ({ ...prev, [lang]: 0 }));
      setCurrentChunkIndex(0);
      setStoryComplete(false);
    }
    
    setIsPlaying(true);
    abortRef.current = false;
    
    const actualStartPos = startPos >= chunks.length ? 0 : startPos;
    
    for (let i = actualStartPos; i < chunks.length; i++) {
      if (abortRef.current) break;
      
      const chunk = chunks[i];
      
      // Update UI
      setCurrentChunkIndex(i);
      setCurrentMahadashaIndex(chunk.mahadashaIndex);
      updateDisplayedText(chunk.text);
      setPlaybackPosition(prev => ({ ...prev, [lang]: i }));
      
      // Get and play audio
      const audioUrl = await getAudioForChunk(chunk.text, lang);
      
      if (audioUrl && !abortRef.current) {
        await playAudioChunk(audioUrl, chunk.text);
      } else if (!abortRef.current) {
        // No audio - wait for reading time
        const readingTime = Math.max(chunk.text.split(/\s+/).length * 300, 3000);
        await new Promise(r => setTimeout(r, readingTime));
      }
      
      // Small pause between chunks
      if (!abortRef.current && i < chunks.length - 1) {
        await new Promise(r => setTimeout(r, 200));
      }
    }
    
    if (!abortRef.current) {
      setPlaybackPosition(prev => ({ ...prev, [lang]: chunks.length }));
      setStoryComplete(true);
    }
    
    setIsPlaying(false);
  }, [selectedLanguage, audioChunks, playbackPosition, getAudioForChunk, playAudioChunk, updateDisplayedText]);

  // Handle play/pause
  const handlePlayPause = () => {
    if (isPlaying) {
      abortRef.current = true;
      stopAudio();
      setIsPlaying(false);
    } else {
      playStory();
    }
  };

  // Handle language change - INSTANT
  const handleLanguageChange = (newLang) => {
    if (newLang === selectedLanguage) return;
    
    // Save current language's position before switching
    setPlaybackPosition(prev => ({ ...prev, [selectedLanguage]: currentChunkIndex }));
    
    abortRef.current = true;
    stopAudio();
    setIsPlaying(false);
    
    setSelectedLanguage(newLang);
    
    const newChunks = audioChunks[newLang];
    const newPos = playbackPosition[newLang] || 0;
    
    if (newChunks && newChunks.length > 0) {
      const displayPos = Math.min(newPos, newChunks.length - 1);
      const chunk = newChunks[displayPos];
      
      if (chunk) {
        updateDisplayedText(chunk.text);
        setCurrentChunkIndex(displayPos);
        setCurrentMahadashaIndex(chunk.mahadashaIndex);
      }
      
      setStoryComplete(newPos >= newChunks.length);
    }
    
    setTimeout(() => { abortRef.current = false; }, 100);
  };

  // Handle replay
  const handleReplay = () => {
    abortRef.current = true;
    stopAudio();
    
    setPlaybackPosition(prev => ({ ...prev, [selectedLanguage]: 0 }));
    setCurrentChunkIndex(0);
    setCurrentMahadashaIndex(-1);
    setStoryComplete(false);
    setIsPlaying(false);
    
    const chunks = audioChunks[selectedLanguage];
    if (chunks && chunks[0]) {
      updateDisplayedText(chunks[0].text);
    }
    
    setTimeout(() => { abortRef.current = false; }, 100);
  };

  // Load story data
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      
      // Check if premium
      if (!isPremium) {
        navigate('/dashboard');
        return;
      }
      
      // Get story data from localStorage
      const storedData = localStorage.getItem('unified_story_data');
      if (!storedData) {
        navigate('/generating-story');
        return;
      }
      
      try {
        const data = JSON.parse(storedData);
        setStoryData(data);
        
        // Get future mahadashas
        const futureMds = data.future_story?.mahadashas || 
                         data.all_mahadashas?.filter(m => m.status === 'future' || m.status === 'current') || [];
        
        // Add colors to mahadashas
        const coloredMds = futureMds.map(md => ({
          ...md,
          color: PLANET_COLORS[md.lord] || domain.color,
          icon: PLANET_ICONS[md.lord] || '☉'
        }));
        
        setFutureMahadashas(coloredMds);
        
        // Generate chunks for all languages
        const newAudioChunks = { hindi: [], hinglish: [], english: [] };
        
        // Get future story sections for this domain
        const domainReels = data.future_story?.domain_reels || {};
        const domainStory = domainReels[domainKey] || {};
        
        for (const lang of LANGUAGES) {
          const storyText = domainStory[lang] || domainStory.hinglish || '';
          
          if (storyText) {
            // Split into sections by double newlines
            const paragraphs = storyText.split(/\n\n+/).filter(p => p.trim().length > 30);
            
            paragraphs.forEach((para, idx) => {
              const chunks = splitTextIntoChunks(para, MAX_CHUNK_CHARS);
              chunks.forEach((chunkText, chunkIdx) => {
                // Map to mahadasha - first 3 are intro/overview, rest map to mahadashas
                const isIntro = idx < 3;
                const mdIdx = isIntro ? -1 : Math.min(idx - 3, coloredMds.length - 1);
                
                newAudioChunks[lang].push({
                  text: chunkText,
                  index: newAudioChunks[lang].length,
                  mahadashaIndex: mdIdx,
                  type: isIntro ? 'intro' : 'mahadasha'
                });
              });
            });
          }
        }
        
        setAudioChunks(newAudioChunks);
        
        // Set initial text
        if (newAudioChunks.hinglish[0]) {
          updateDisplayedText(newAudioChunks.hinglish[0].text);
        }
        
        console.log(`📊 Future ${domainKey} chunks: Hindi=${newAudioChunks.hindi.length}, Hinglish=${newAudioChunks.hinglish.length}, English=${newAudioChunks.english.length}`);
        
      } catch (error) {
        console.error('Error loading future story:', error);
      }
      
      setIsLoading(false);
    };
    
    loadData();
  }, [domainKey, isPremium, navigate, updateDisplayedText, domain.color]);

  // Cleanup
  useEffect(() => {
    return () => {
      abortRef.current = true;
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
    };
  }, []);

  // Loading screen
  if (isLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'radial-gradient(ellipse at center, rgba(237, 193, 97, 0.08) 0%, #020712 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>{domain.icon}</div>
          <p style={{ color: 'rgba(255,255,255,0.6)' }}>Loading your {domain.name} future...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      height: '100vh',
      maxHeight: '100vh',
      overflow: 'hidden',
      background: 'radial-gradient(ellipse at center, rgba(237, 193, 97, 0.08) 0%, #020712 100%)',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative'
    }}>
      
      {/* ===== TOP: Header (~8%) ===== */}
      <div style={{ 
        flex: '0 0 auto',
        padding: 'clamp(8px, 2vw, 12px) clamp(12px, 4vw, 20px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '1px solid rgba(237, 193, 97, 0.1)'
      }}>
        <button
          onClick={() => navigate('/dashboard')}
          style={{
            background: 'rgba(255,255,255,0.05)',
            border: 'none',
            borderRadius: '50%',
            width: '36px',
            height: '36px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer'
          }}
        >
          <ArrowLeft size={18} color="rgba(255,255,255,0.7)" />
        </button>
        
        <div style={{ textAlign: 'center' }}>
          <p style={{ 
            fontSize: 'clamp(14px, 4vw, 18px)', 
            fontWeight: 700, 
            color: domain.color,
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span>{domain.icon}</span>
            {domain.name} Future
          </p>
        </div>
        
        <div style={{ width: '36px' }} />
      </div>

      {/* ===== LANGUAGE PILLS (~6%) ===== */}
      <div style={{
        flex: '0 0 auto',
        padding: 'clamp(6px, 1.5vw, 10px)',
        display: 'flex',
        justifyContent: 'center',
        gap: 'clamp(6px, 2vw, 10px)'
      }}>
        {[
          { key: 'hindi', label: 'हिंदी' },
          { key: 'hinglish', label: 'Hinglish' },
          { key: 'english', label: 'English' }
        ].map((lang) => (
          <button
            key={lang.key}
            onClick={() => handleLanguageChange(lang.key)}
            style={{
              padding: 'clamp(6px, 1.5vw, 10px) clamp(12px, 3vw, 20px)',
              borderRadius: '20px',
              border: 'none',
              background: selectedLanguage === lang.key 
                ? `linear-gradient(135deg, ${domain.color}, ${domain.color}88)`
                : 'rgba(255,255,255,0.08)',
              color: selectedLanguage === lang.key ? '#fff' : 'rgba(255,255,255,0.6)',
              fontSize: 'clamp(11px, 3vw, 14px)',
              fontWeight: selectedLanguage === lang.key ? 700 : 500,
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            {lang.label}
          </button>
        ))}
      </div>

      {/* ===== MIDDLE: Sage Image + Text Box (~50%) ===== */}
      <div style={{
        flex: '1 1 auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'clamp(8px, 2vw, 16px)',
        minHeight: 0,
        overflow: 'hidden'
      }}>
        {/* Sage Image */}
        <div style={{
          width: 'clamp(80px, 20vw, 120px)',
          height: 'clamp(80px, 20vw, 120px)',
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${domain.color}30, ${domain.color}10)`,
          border: `2px solid ${domain.color}50`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 'clamp(12px, 3vw, 20px)',
          boxShadow: `0 0 30px ${domain.color}30`
        }}>
          <span style={{ fontSize: 'clamp(36px, 10vw, 56px)' }}>{domain.icon}</span>
        </div>

        {/* Play/Pause Button */}
        <button
          onClick={handlePlayPause}
          style={{
            width: 'clamp(50px, 14vw, 70px)',
            height: 'clamp(50px, 14vw, 70px)',
            borderRadius: '50%',
            background: isPlaying 
              ? 'rgba(255,255,255,0.15)'
              : `linear-gradient(135deg, ${domain.color}, ${domain.color}88)`,
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            marginBottom: 'clamp(12px, 3vw, 20px)',
            boxShadow: isPlaying ? 'none' : `0 4px 20px ${domain.color}40`
          }}
        >
          {isPlaying ? (
            <Pause size={24} color="#fff" fill="#fff" />
          ) : storyComplete ? (
            <RotateCcw size={24} color="#fff" />
          ) : (
            <Play size={24} color="#fff" fill="#fff" style={{ marginLeft: '3px' }} />
          )}
        </button>

        {/* Text Box */}
        {displayedText && (
          <div style={{ width: '100%', maxWidth: '500px' }}>
            <div style={{
              width: 0,
              height: 0,
              margin: '0 auto',
              borderLeft: '10px solid transparent',
              borderRight: '10px solid transparent',
              borderBottom: `10px solid ${currentMahadasha.color || domain.color}40`
            }} />
            
            <div style={{
              padding: 'clamp(12px, 3vw, 18px)',
              background: `linear-gradient(135deg, ${currentMahadasha.color || domain.color}18, rgba(2, 7, 18, 0.95))`,
              backdropFilter: 'blur(20px)',
              borderRadius: 'clamp(12px, 3vw, 16px)',
              border: `1.5px solid ${currentMahadasha.color || domain.color}50`,
              minHeight: 'clamp(100px, 20vh, 140px)',
              maxHeight: 'clamp(120px, 25vh, 160px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              transition: 'border-color 0.5s ease, background 0.5s ease'
            }}>
              <p style={{
                fontSize: 'clamp(13px, 3.5vw, 16px)',
                lineHeight: '1.8',
                color: '#F0F0F0',
                margin: 0,
                textAlign: 'center',
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 4,
                WebkitBoxOrient: 'vertical'
              }}>
                {displayedText}
              </p>
            </div>

            {/* Current Mahadasha Indicator */}
            {currentMahadashaIndex >= 0 && currentMahadasha.lord && (
              <div style={{ 
                textAlign: 'center', 
                marginTop: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px'
              }}>
                <span style={{ 
                  fontSize: 'clamp(20px, 5vw, 28px)', 
                  textShadow: `0 0 15px ${currentMahadasha.color || domain.color}` 
                }}>
                  {currentMahadasha.icon || PLANET_ICONS[currentMahadasha.lord] || '☉'}
                </span>
                <p style={{
                  fontSize: 'clamp(10px, 2.8vw, 12px)',
                  fontWeight: 600,
                  color: currentMahadasha.color || domain.color,
                  margin: 0
                }}>
                  {currentMahadasha.lord} Mahadasha • {currentMahadasha.years}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ===== BOTTOM: Timeline (~20%) ===== */}
      <div style={{ 
        flex: '0 0 auto',
        padding: 'clamp(8px, 2vw, 12px) clamp(12px, 4vw, 20px)',
        borderTop: '1px solid rgba(237, 193, 97, 0.1)'
      }}>
        <p style={{
          fontSize: 'clamp(9px, 2.5vw, 11px)',
          color: 'rgba(255,255,255,0.4)',
          textAlign: 'center',
          marginBottom: '8px',
          textTransform: 'uppercase',
          letterSpacing: '1px'
        }}>
          Future Mahadasha Timeline
        </p>
        
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 'clamp(4px, 1.5vw, 8px)',
          flexWrap: 'nowrap',
          overflowX: 'auto',
          padding: '4px 0'
        }}>
          {futureMahadashas.map((md, idx) => {
            const isActive = idx === currentMahadashaIndex;
            const isPast = idx < currentMahadashaIndex;
            const mdColor = md.color || PLANET_COLORS[md.lord] || domain.color;
            
            return (
              <div key={idx} style={{ 
                display: 'flex', 
                alignItems: 'center',
                flexShrink: 0
              }}>
                {/* Mahadasha Circle */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <div style={{
                    width: isActive ? 'clamp(36px, 10vw, 48px)' : 'clamp(28px, 8vw, 36px)',
                    height: isActive ? 'clamp(36px, 10vw, 48px)' : 'clamp(28px, 8vw, 36px)',
                    borderRadius: '50%',
                    background: isActive 
                      ? `linear-gradient(135deg, ${mdColor}, ${mdColor}88)`
                      : isPast 
                        ? `${mdColor}40`
                        : 'rgba(255,255,255,0.08)',
                    border: isActive ? `2px solid ${mdColor}` : '1px solid rgba(255,255,255,0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s ease',
                    boxShadow: isActive ? `0 0 20px ${mdColor}50` : 'none',
                    animation: isActive ? 'pulse 2s infinite' : 'none'
                  }}>
                    <span style={{ 
                      fontSize: isActive ? 'clamp(16px, 4vw, 20px)' : 'clamp(12px, 3vw, 16px)',
                      opacity: isActive ? 1 : isPast ? 0.6 : 0.4
                    }}>
                      {md.icon || PLANET_ICONS[md.lord] || '☉'}
                    </span>
                  </div>
                  
                  {/* Years label */}
                  <p style={{
                    fontSize: 'clamp(8px, 2vw, 10px)',
                    color: isActive ? mdColor : 'rgba(255,255,255,0.4)',
                    margin: 0,
                    whiteSpace: 'nowrap',
                    fontWeight: isActive ? 600 : 400
                  }}>
                    {md.years?.split('-')[0] || ''}
                  </p>
                </div>
                
                {/* Connector line */}
                {idx < futureMahadashas.length - 1 && (
                  <div style={{
                    width: 'clamp(12px, 4vw, 24px)',
                    height: '2px',
                    background: isPast ? `${mdColor}60` : 'rgba(255,255,255,0.1)',
                    margin: '0 2px'
                  }} />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Pulse animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
      `}</style>
    </div>
  );
};

export default FutureStoryUnified;
